// Context hooks - replacing Apollo
import { useResult } from "../../../shared/formContext/hooks/useResult";

// Result Types
import Error from "@shared/Error/Error";
import SavingsCertificates from "./Result/SavingsCertificates";

// Components
import Empty from "@shared/Empty/Empty";
import { __ } from "@wordpress/i18n";
import React from "react";
import Loading from "../../../components/Loading/Loading";

/**
 * BiG Bank URL for banner and logo(with UTM tracking)
 */
const BIG_BANK_BANNER_URL = "https://www.big.pt/?utm_source=doutorfinancas&utm_medium=simulador";

/**
 * UI message constants with WordPress i18n support
 */
const UI_MESSAGES = {
  LOADING: __("Estamos a preparar a sua simulação", "df-simulators"),
  ERROR_STATUS: "Ocorreu um erro ao carregar os resultados.",
  NO_RESULTS: __("Não há resultados disponíveis no momento.", "df-simulators"),
  BIG: __("Em parceria com", "df-simulators"),
  BIG_VISIT: __("Visitar website do BiG", "df-simulators"),
};

/**
 * Results Component
 *
 * Displays simulation results based on current state
 * Uses Context API instead of Apollo for state management
 * Includes clickable BiG logo in empty state
 *
 * Handles different states:
 * - Loading: Shows loading spinner
 * - Empty: Shows empty state (desktop only) with clickable BiG logo
 * - Error: Shows error component
 * - Success: Shows simulation results
 */
const Results: React.FC = () => {
  // Use Context hook instead of Apollo reactive variables
  const { resultData } = useResult();
  const { response, loading, statusCode } = resultData;

  const handleBannerClick = () => {
    window.open(BIG_BANK_BANNER_URL, "_blank", "noopener,noreferrer");
  };

  /**
   * Renders the appropriate result component based on the current state
   *
   * @returns JSX element representing the current result state
   */
  const renderResult = () => {
    // Show loading component during API calls
    if (loading) {
      return <Loading message={UI_MESSAGES.LOADING} />;
    }

    // Show empty state when no response is available
    if (!response) {
      return (
        <>
          {/* Desktop: empty state with BiG logo */}
          <div className="hidden lg:flex flex-col gap-12 mb-12">
            <Empty message={UI_MESSAGES.NO_RESULTS} figure="6" />
          </div>
          {/* Banner BiG: visible on all breakpoints */}
          <div className="flex justify-center">
            <button
              onClick={handleBannerClick}
              className="cursor-pointer hover:opacity-80 transition-opacity duration-200 bg-transparent border-none p-0 w-full"
              aria-label={UI_MESSAGES.BIG_VISIT}
            >
              <img
                src="/wp-content/plugins/df-simulators/src/blocks/savings-certificates/assets/img/Doutor-Financas-Simulador-Certificados-Aforro-Display-Ad-BiG.webp"
                alt={UI_MESSAGES.BIG_VISIT}
                className="w-full h-auto rounded-lg"
              />
            </button>
          </div>
        </>
      );
    }

    // Show error component for non-200 status codes
    if (statusCode && statusCode !== 200) {
      console.warn(UI_MESSAGES.ERROR_STATUS);
      return <Error />;
    }

    // Show successful simulation results
    return <SavingsCertificates />;
  };

  return (
    <div className="tool-block__component__results__result">
      {renderResult()}
    </div>
  );
};

export default Results;
